import {
  Button,
  Checkbox,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import type { Column, Row, Table } from "@tanstack/react-table";
import {
  type ChangeEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import type {
  BodyColumnMeta,
  BodyRow,
  BodyRowSelectionMode,
} from "../../types";
import { getColumnMeta, idsToSelectionState } from "../helpers";

type ColumnVisibilityControlProps<Row extends BodyRow> = {
  columnsToControl: Column<Row, unknown>[];
  columnLabelById: Map<string, string>;
  isResetDisabled: boolean;
  onResetColumnPreferences: () => void;
  pinnedColumnIds?: string[];
  onPinColumn?: (id: string) => void;
  onUnpinColumn?: (id: string) => void;
};
export const ColumnVisibilityControl = <Row extends BodyRow>({
  columnsToControl,
  columnLabelById,
  isResetDisabled,
  onResetColumnPreferences,
  pinnedColumnIds = [],
  onPinColumn,
  onUnpinColumn,
}: ColumnVisibilityControlProps<Row>) => {
  return (
    <Popover isLazy placement="bottom-end" strategy="fixed">
      <PopoverTrigger>
        <IconButton
          aria-label={t`Toggle visible columns`}
          icon={<Icon name="menu-01" transform="auto" rotate={90} />}
          variant="ghost"
        />
      </PopoverTrigger>

      <Portal>
        <PopoverContent overflowY="auto" w="260px" maxH="420px">
          <PopoverBody px="4" py="3" fontWeight="normal">
            <Text mb="3" color="gray.700" fontSize="14px" fontWeight="medium">
              <Trans>Columns</Trans>
            </Text>

            <Stack spacing="3">
              {columnsToControl.map((column) => {
                return (
                  <HStack key={column.id} justify="space-between" spacing="2">
                    <Checkbox
                      isChecked={column.getIsVisible()}
                      onChange={(event) => {
                        column.toggleVisibility(event.target.checked);
                      }}
                    >
                      {columnLabelById.get(column.id) ?? column.id}
                    </Checkbox>

                    <ColumnPinButton
                      column={column}
                      pinnedColumnIds={pinnedColumnIds}
                      onPinColumn={onPinColumn}
                      onUnpinColumn={onUnpinColumn}
                    />
                  </HStack>
                );
              })}
            </Stack>

            <Button
              w="full"
              mt="2"
              colorScheme="red"
              isDisabled={isResetDisabled}
              onClick={onResetColumnPreferences}
              size="sm"
              variant="ghost"
            >
              <Trans>Reset column settings</Trans>
            </Button>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

interface ColumnPinButtonProps<Row extends BodyRow>
  extends Pick<
    ColumnVisibilityControlProps<Row>,
    "pinnedColumnIds" | "onPinColumn" | "onUnpinColumn"
  > {
  column: Column<Row, unknown>;
}
const ColumnPinButton = <Row extends BodyRow>({
  column,
  pinnedColumnIds,
  onPinColumn,
  onUnpinColumn,
}: ColumnPinButtonProps<Row>) => {
  const columnMeta: BodyColumnMeta | undefined = getColumnMeta(
    column.columnDef,
  );
  const isPinned = pinnedColumnIds?.includes(column.id) ?? false;
  const isPinningDisabled = columnMeta?.isPinningDisabled ?? false;
  const canPin = column.getCanPin() && !isPinningDisabled;

  return (
    canPin && (
      <Tooltip
        hasArrow
        label={isPinned ? t`Unpin column` : t`Pin column`}
        placement="right"
      >
        <IconButton
          w="6"
          h="6"
          aria-label={isPinned ? t`Unpin column` : t`Pin column`}
          icon={isPinned ? <Icon name="x-close" /> : <Icon name="pin-01" />}
          onClick={(event) => {
            event.stopPropagation();
            if (isPinned && onUnpinColumn) {
              onUnpinColumn(column.id);
            } else if (!isPinned && onPinColumn) {
              onPinColumn(column.id);
            }
          }}
          size="xs"
          variant="ghost"
        />
      </Tooltip>
    )
  );
};

type SelectionHeaderCellProps<Row> = {
  table: Table<Row>;
  pageSelectableIds: string[];
  rowSelectionMode: BodyRowSelectionMode | undefined;
  selectedIds: Set<string>;
};
export const SelectionHeaderCell = <Row extends BodyRow>({
  table,
  pageSelectableIds,
  rowSelectionMode,
  selectedIds,
}: SelectionHeaderCellProps<Row>) => {
  if (rowSelectionMode === undefined || rowSelectionMode === "rows") {
    return (
      <Checkbox
        aria-label={t`Select all rows`}
        isChecked={table.getIsAllRowsSelected()}
        isIndeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    );
  }

  const selectedOnPage = pageSelectableIds.filter((id) =>
    selectedIds.has(id),
  ).length;
  const isChecked =
    pageSelectableIds.length > 0 && selectedOnPage === pageSelectableIds.length;
  const isIndeterminate =
    selectedOnPage > 0 && selectedOnPage < pageSelectableIds.length;

  return (
    <Checkbox
      aria-label={t`Select all rows`}
      isChecked={isChecked}
      isIndeterminate={isIndeterminate}
      onChange={(event) => {
        const nextSelectedIds = event.target.checked ? pageSelectableIds : [];
        table.setRowSelection(idsToSelectionState(nextSelectedIds));
      }}
    />
  );
};

type SelectionCellProps<RowDataType> = {
  row: Row<RowDataType>;
  table: Table<RowDataType>;
  rowSelectionMode: BodyRowSelectionMode | undefined;
  selectedIds: Set<string>;
};
export const SelectionCell = <RowDataType extends BodyRow>({
  row,
  table,
  rowSelectionMode,
  selectedIds,
}: SelectionCellProps<RowDataType>) => {
  if (!row.getCanSelect()) {
    return null;
  }

  if (rowSelectionMode === undefined || rowSelectionMode === "rows") {
    return (
      <Checkbox
        aria-label={t`Select row`}
        isChecked={row.getIsSelected()}
        isIndeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    );
  }

  const subRowIds =
    row.depth === 0
      ? (row.original.__subRows?.map((subRow) => subRow.id) ?? [])
      : [];
  const rowDomainId = row.original.id;
  const parentDomainId = row.getParentRow()?.original.id;
  let isChecked = selectedIds.has(rowDomainId);
  let isIndeterminate = false;

  const updateSelection = (nextSelectedIds: Set<string>): void => {
    table.setRowSelection(idsToSelectionState(nextSelectedIds));
  };

  if (rowSelectionMode === "subrows") {
    if (row.depth === 0 && subRowIds.length > 0) {
      const selectedSubRowsCount = subRowIds.filter((id) =>
        selectedIds.has(id),
      ).length;
      isChecked = selectedSubRowsCount === subRowIds.length;
      isIndeterminate =
        selectedSubRowsCount > 0 && selectedSubRowsCount < subRowIds.length;
    }

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const shouldSelect = event.target.checked;

      if (row.depth === 0 && subRowIds.length > 0) {
        subRowIds.forEach((id) => {
          if (shouldSelect) {
            selectedIds.add(id);
          } else {
            selectedIds.delete(id);
          }
        });
      } else if (shouldSelect) {
        selectedIds.add(rowDomainId);
      } else {
        selectedIds.delete(rowDomainId);
      }

      updateSelection(selectedIds);
    };

    return (
      <Checkbox
        aria-label={t`Select row`}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        onChange={handleOnChange}
      />
    );
  }

  if (rowSelectionMode === "subrowsOrParent") {
    if (row.depth === 0 && subRowIds.length > 0) {
      const selectedSubRowsCount = subRowIds.filter((id) =>
        selectedIds.has(id),
      ).length;
      const areAllSubRowsSelected = selectedSubRowsCount === subRowIds.length;

      isChecked = areAllSubRowsSelected || selectedIds.has(rowDomainId);
      isIndeterminate = !isChecked && selectedSubRowsCount > 0;
    }

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const shouldSelect = event.target.checked;

      if (row.depth === 0 && subRowIds.length > 0) {
        const idsToToggle = [rowDomainId, ...subRowIds];

        idsToToggle.forEach((id) => {
          if (shouldSelect) {
            selectedIds.add(id);
          } else {
            selectedIds.delete(id);
          }
        });
      } else if (row.depth > 0) {
        if (shouldSelect) {
          selectedIds.add(rowDomainId);
        } else {
          selectedIds.delete(rowDomainId);
          if (parentDomainId) {
            selectedIds.delete(parentDomainId);
          }
        }
      } else if (shouldSelect) {
        selectedIds.add(rowDomainId);
      } else {
        selectedIds.delete(rowDomainId);
      }

      updateSelection(selectedIds);
    };

    return (
      <Checkbox
        aria-label={t`Select row`}
        isChecked={isChecked}
        isIndeterminate={isIndeterminate}
        onChange={handleOnChange}
      />
    );
  }

  return (
    <Checkbox
      aria-label={t`Select row`}
      isChecked={isChecked}
      onChange={(event) => {
        if (event.target.checked) {
          selectedIds.add(rowDomainId);
        } else {
          selectedIds.delete(rowDomainId);
        }

        updateSelection(selectedIds);
      }}
    />
  );
};

type TruncatedTextWithTooltipProps = {
  text: ReactNode;
  tooltip?: ReactNode;
  maxWidth?: string;
};
export const TruncatedTextWithTooltip = ({
  text,
  tooltip,
}: TruncatedTextWithTooltipProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const updateTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    updateTruncation();

    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [text]);

  if (!text) {
    return null;
  }

  return (
    <Tooltip
      hasArrow
      isDisabled={!isTruncated}
      label={tooltip ?? text}
      placement="top"
    >
      <span
        ref={textRef}
        style={{
          display: "block",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </span>
    </Tooltip>
  );
};
